import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  getAllSportsAdmin,
  deleteSport,
  toggleSportStatus,
  updateSportPopularity,
} from "../../services/api/sportsService";
import GameLayout from "../layout/GameLayout";
import FloatingCard from "../ui/FloatingCard";
import FloatingButton from "../ui/FloatingButton";
import StatusBadge from "../ui/StatusBadge";
import "../../styles/features/sports.css";

const SportsAdminManagement = ({ user, token, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [sports, setSports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(location.state?.successMessage || "");

  // Check user permissions - Fixed to handle both data formats
  const hasPermission = (permission) => {
    if (user.role === "super_admin" || user.roleName === "super_admin")
      return true;

    // Handle both array of strings and array of objects
    if (Array.isArray(user.permissions)) {
      return user.permissions.some((p) =>
        typeof p === "string" ? p === permission : p.name === permission
      );
    }
    return false;
  };

  const canWrite =
    hasPermission("sports.write") || hasPermission("sports_management");
  const canDelete =
    hasPermission("sports.delete") || hasPermission("sports_management");
  const canAdmin =
    hasPermission("sports.admin") || hasPermission("sports_management");

  useEffect(() => {
    fetchSports();
    // Clear success message from location state after reading so it doesn't reappear on refresh
    if (location.state?.successMessage) {
      window.history.replaceState({}, document.title);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (success) {
      const t = setTimeout(() => setSuccess(""), 4000);
      return () => clearTimeout(t);
    }
  }, [success]);

  const fetchSports = async () => {
    try {
      setLoading(true);
      const response = await getAllSportsAdmin();
      const sportsData = response.data || [];
      setSports(sportsData);
      setError("");
    } catch (err) {
      setError("Failed to fetch sports: " + err.message);
      setSports([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSport = async (sportId, sportName) => {
    if (!canDelete) {
      setError("You do not have permission to delete sports");
      return;
    }
    if (!window.confirm(`Are you sure you want to delete "${sportName}"?`))
      return;
    try {
      setLoading(true);
      await deleteSport(token, sportId);
      setSuccess("Sport deleted successfully!");
      fetchSports();
    } catch (err) {
      setError(
        "Failed to delete sport: " + (err.response?.data?.message || err.message)
      );
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (sportId, currentStatus) => {
    if (!canAdmin) {
      setError("You do not have permission to change sport status");
      return;
    }
    try {
      setLoading(true);
      await toggleSportStatus(token, sportId);
      setSuccess(`Sport ${currentStatus ? "disabled" : "enabled"} successfully!`);
      fetchSports();
    } catch (err) {
      setError(
        "Failed to toggle sport status: " + (err.response?.data?.message || err.message)
      );
    } finally {
      setLoading(false);
    }
  };

  const handlePopularityUpdate = async (sportId, action) => {
    if (!canAdmin) {
      setError("You do not have permission to update sport popularity");
      return;
    }
    try {
      await updateSportPopularity(token, sportId, action);
      setSuccess(`Sport popularity ${action}ed successfully!`);
      fetchSports();
    } catch (err) {
      setError(
        "Failed to update popularity: " + (err.response?.data?.message || err.message)
      );
    }
  };

  if (loading && sports.length === 0) {
    return <div className="loading">Loading sports...</div>;
  }

  return (
    <GameLayout
      token={token}
      onLogout={onLogout}
      profile={user}
      showFloatingNav={true}
    >
      <div className="sports-admin-gaming-layout">
        {/* Gaming Admin Header - Floating */}
        <div className="admin-header-gaming-floating">
          <FloatingCard variant="primary" size="large" className="delay-1">
            <div className="admin-header-content">
              <div className="admin-title-section">
                <h2 className="admin-title-gaming">🏅 Sports Administration</h2>
                <p className="admin-subtitle-gaming">
                  Manage sports, categories, and configurations
                </p>
              </div>

              <div className="admin-info-gaming">
                <div className="role-display">
                  <span className="role-badge-gaming">
                    👑 {user.role || user.roleName || "Unknown Role"}
                  </span>
                </div>

                <div className="permissions-gaming">
                  <div
                    className={`permission-item ${canWrite ? "granted" : "denied"}`}
                  >
                    <span className="perm-icon">✏️</span>
                    <span className="perm-text">Write</span>
                    <span className="perm-status">
                      {canWrite ? "✅" : "❌"}
                    </span>
                  </div>
                  <div
                    className={`permission-item ${canDelete ? "granted" : "denied"}`}
                  >
                    <span className="perm-icon">🗑️</span>
                    <span className="perm-text">Delete</span>
                    <span className="perm-status">
                      {canDelete ? "✅" : "❌"}
                    </span>
                  </div>
                  <div
                    className={`permission-item ${canAdmin ? "granted" : "denied"}`}
                  >
                    <span className="perm-icon">👑</span>
                    <span className="perm-text">Admin</span>
                    <span className="perm-status">
                      {canAdmin ? "✅" : "❌"}
                    </span>
                  </div>
                </div>

                <div className="admin-actions-gaming">
                  <FloatingButton
                    variant="secondary"
                    size="medium"
                    onClick={fetchSports}
                    disabled={loading}
                    icon="🔄"
                    loading={loading}
                  >
                    <span className="btn-text">
                      {loading ? "Refreshing..." : "Refresh"}
                    </span>
                  </FloatingButton>
                </div>
              </div>
            </div>
          </FloatingCard>
        </div>

        {/* Gaming Messages - Floating */}
        {error && (
          <div className="gaming-message-floating error">
            <span className="message-icon">❌</span>
            <span className="message-text">{error}</span>
          </div>
        )}
        {success && (
          <div className="gaming-message-floating success">
            <span className="message-icon">✅</span>
            <span className="message-text">{success}</span>
          </div>
        )}

        {/* Create Sport Action - Floating */}
        {canWrite && (
          <div className="create-sport-floating">
            <FloatingButton
              variant="success"
              size="large"
              onClick={() => navigate("/sports-admin/create")}
              icon="➕"
              className="delay-2"
            >
              <span className="btn-text">Create New Sport</span>
            </FloatingButton>
          </div>
        )}

        {/* Gaming Sports Grid - Floating */}
        <div className="sports-grid-gaming-floating">
          {Array.isArray(sports) &&
            sports.map((sport, index) => (
              <FloatingCard
                key={sport.id}
                variant="outline"
                size="medium"
                className={`delay-${(index % 5) + 4}${sport.isActive ? "" : " sport-card-inactive"}`}
              >
                <div className="sport-card-gaming">
                  <div className="sport-header-gaming">
                    <h3 className="sport-name-gaming">{sport.name}</h3>
                    <div className="sport-status-gaming">
                      <StatusBadge badge={sport.isActive ? { label: "Active", tone: "success" } : { label: "Inactive", tone: "muted" }} />
                    </div>
                  </div>

                  <div className="sport-details-gaming">
                    <div className="detail-item">
                      <span className="detail-icon">📂</span>
                      <span className="detail-label">Category:</span>
                      <span className="detail-value">{sport.category}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-icon">👥</span>
                      <span className="detail-label">Players:</span>
                      <span className="detail-value">
                        {sport.minPlayers || "N/A"} -{" "}
                        {sport.maxPlayers || "N/A"}
                      </span>
                    </div>
                    {sport.description && (
                      <div className="detail-item full-width">
                        <span className="detail-icon">📖</span>
                        <span className="detail-label">Description:</span>
                        <p className="detail-description">
                          {sport.description}
                        </p>
                      </div>
                    )}
                    {sport.popularityScore && (
                      <div className="detail-item">
                        <span className="detail-icon">⭐</span>
                        <span className="detail-label">Popularity:</span>
                        <span className="detail-value">
                          {sport.popularityScore}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="sport-actions-gaming">
                    {canWrite && (
                      <FloatingButton
                        variant="secondary"
                        size="small"
                        onClick={() => navigate(`/sports-admin/edit/${sport.id}`)}
                        icon="✏️"
                      >
                        <span className="btn-text">Edit</span>
                      </FloatingButton>
                    )}

                    {canAdmin && (
                      <>
                        <FloatingButton
                          variant={sport.isActive ? "warning" : "success"}
                          size="small"
                          onClick={() =>
                            handleToggleStatus(sport.id, sport.isActive)
                          }
                          icon={sport.isActive ? "⏸️" : "▶️"}
                        >
                          <span className="btn-text">
                            {sport.isActive ? "Disable" : "Enable"}
                          </span>
                        </FloatingButton>

                        <FloatingButton
                          variant="primary"
                          size="small"
                          onClick={() =>
                            handlePopularityUpdate(sport.id, "increment")
                          }
                          icon="👍"
                        >
                          <span className="btn-text">+1</span>
                        </FloatingButton>
                      </>
                    )}

                    {canDelete && (
                      <FloatingButton
                        variant="danger"
                        size="small"
                        onClick={() => handleDeleteSport(sport.id, sport.name)}
                        icon="🗑️"
                      >
                        <span className="btn-text">Delete</span>
                      </FloatingButton>
                    )}
                  </div>
                </div>
              </FloatingCard>
            ))}
        </div>

        {/* Empty State - Gaming */}
        {sports.length === 0 && !loading && (
          <div className="empty-state-gaming-floating">
            <FloatingCard variant="outline" size="large" className="delay-2">
              <div className="empty-content-gaming">
                <div className="empty-icon">🏅</div>
                <h3>No Sports Found</h3>
                <p>Start by creating your first sport!</p>
                {canWrite && (
                  <FloatingButton
                    variant="primary"
                    size="medium"
                    onClick={() => navigate("/sports-admin/create")}
                    icon="➕"
                  >
                    <span className="btn-text">Create First Sport</span>
                  </FloatingButton>
                )}
              </div>
            </FloatingCard>
          </div>
        )}

        {/* Loading State - Gaming */}
        {loading && (
          <div className="loading-gaming-floating">
            <FloatingCard variant="outline" size="medium">
              <div className="loading-content-gaming">
                <div className="loading-spinner-gaming"></div>
                <p>Loading sports...</p>
              </div>
            </FloatingCard>
          </div>
        )}
      </div>
    </GameLayout>
  );
};

export default SportsAdminManagement;
